/**
 * Output schemas for trigger nodes. The "output" of a trigger is what
 * `ctx.input` will contain when the workflow runs — the editor reads it
 * to populate the template-tag picker with `input.<field>` entries, and
 * dataflow validation uses it to reject typos like `input.subjct`.
 *
 * Triggers don't have config or input schemas in the contract sense; the
 * registry slot is filled with `z.object({}).passthrough()` so callers
 * that ask for `inputSchema` / `configSchema` get a permissive shape.
 *
 * Sources of truth (what these schemas mirror):
 * - `email_received` — `apps/worker/src/email-messages.ts` — the
 *   payload `createWorkflowRunAndDispatch` is given when an inbound email
 *   matches a trigger.
 * - `manual` — free-form: API callers pass an arbitrary `input` object
 *   (`apps/api/src/routes/public-v1/workflow-runs.ts:252`), so we declare a
 *   passthrough record with the always-present `startedAt`.
 * - `record_created` — `apps/worker/src/workflow-triggers.ts` — the payload a
 *   `custom_record.created` event becomes through the shared object-change
 *   delivery path.
 * - `schedule` — `apps/worker/src/schedules-poller.ts` — the cron poller
 *   merges the trigger's configured `input` object into `ctx.input` and
 *   stamps `startedAt`, so the output is `startedAt` plus whatever keys the
 *   author put in the schedule's input (hence the passthrough shape).
 * - `task_updated` — `apps/worker/src/workflow-triggers.ts` — the payload a
 *   `task.updated` event becomes when a published trigger matches it.
 */

import { z } from "zod";
import { PLATFORM_EVENT_DEFINITIONS } from "../platform-events";
import { WORKFLOW_TRIGGER_CATALOG } from "../workflow-trigger-catalog";

// Type-only import to avoid the circular hazard (`index.ts` re-exports
// from this file). `NodeContract` is structural, so the cycle resolves
// at compile time without runtime fallout.
import type { NodeContract } from "./index";

const triggerInputSchema = z.object({}).passthrough();
const triggerConfigSchema = z.object({}).passthrough();

const manualOutputSchema = z
  .object({
    startedAt: z.string().describe("ISO 8601 timestamp when the run was queued."),
    /**
     * Conventional field for manual runs kicked off against a set of
     * files (the most common case — `apps/web/src/routes/workflow-detail-page.tsx`
     * dispatches with this shape, and find_or_create_* nodes look for
     * it as the implicit `fileIds` fallback). Declared optional because
     * record-driven manual runs don't include it.
     */
    fileIds: z
      .array(z.string())
      .optional()
      .describe("Public ids of files attached to the manual run, when applicable."),
    /**
     * Optional record context — set when the manual run was triggered
     * from a record detail page. `apps/worker/src/workflows.ts` reads
     * this to scope record-bound workflow runs.
     */
    recordId: z
      .string()
      .optional()
      .describe("Public id of the record this run is scoped to, if any."),
    /**
     * Optional inbound email id — set when a workflow is re-run
     * manually against a previously-received email (the inbound-email
     * dispatcher uses this shape for replay).
     */
    inboundEmailId: z
      .string()
      .optional()
      .describe("Public id of an inbound email this run replays, if any."),
  })
  .passthrough();

const emailReceivedOutputSchema = z.object({
  inboundEmailId: z.string().describe("Public id of the email message row (`emsg_*`)."),
  // Named as the `tag_applied` trigger names it, so one `input.resourceId`
  // binds whichever trigger fired; absent when the message has no thread.
  resourceId: z
    .string()
    .optional()
    .describe("Public id of the email thread this message landed on (`eth_*`) — taggable."),
  from: z.string().describe("Sender email address."),
  to: z.string().describe("Recipient email address — the inbound mailbox that matched."),
  subject: z.string().describe("Email subject (empty string when missing)."),
  body: z.string().describe("Plain-text body extracted from the email."),
  attachmentCount: z.number().int().describe("Number of attachments on the email."),
  fileIds: z
    .array(z.string())
    .describe("Public ids of stored attachment files, ready for downstream nodes."),
  resourceFileIds: z
    .record(z.string(), z.string())
    .optional()
    .describe("Optional named file ids supplied by source-specific inbox payloads."),
  startedAt: z.string().describe("ISO 8601 timestamp when the run was queued."),
});

const recordCreatedOutputSchema = PLATFORM_EVENT_DEFINITIONS["custom_record.created"].data
  .extend({
    recordId: z.string().describe("Public id of the record that fired the trigger."),
    startedAt: z.string().describe("ISO 8601 timestamp when the run was queued."),
  })
  .passthrough();

const scheduleOutputSchema = z
  .object({
    startedAt: z.string().describe("ISO 8601 timestamp when the scheduled run was queued."),
  })
  .passthrough();

const tagAppliedOutputSchema = z.object({
  resourceId: z.string().describe("Public id of the resource that was tagged (`eth_*` today)."),
  resourceType: z.string().describe("Taggable resource type, derived from the id prefix."),
  event: z.string().describe('Either "applied" or "removed".'),
  tag: z
    .object({
      id: z.string().describe("Public id of the tag (`tag_*`)."),
      name: z.string().describe("Tag name as shown in the catalog."),
      color: z.string().describe("Palette key for the tag chip."),
    })
    .describe("The tag whose application changed."),
  actor: z
    .object({
      userId: z.string().nullable().describe("Public id of the user who tagged, if a person did."),
      agentThreadId: z.string().nullable().describe("Agent thread that tagged, if an agent did."),
      workflowRunId: z.string().nullable().describe("Workflow run that tagged, if a run did."),
    })
    .describe("Who applied or removed the tag; at most one field is set."),
  triggerChain: z
    .array(z.string())
    .describe(
      "Public ids of the workflows already run in this tagging chain; a workflow in it is never re-entered.",
    ),
  startedAt: z.string().describe("ISO 8601 timestamp when the run was queued."),
});

const taskUpdatedOutputSchema = z.object({
  taskId: z.string().describe("Public id of the task that changed (`tsk_*`)."),
  // Named as the tag trigger names it, so one `input.resourceId` binds whichever trigger fired.
  resourceId: z.string().describe("The same task id — taggable."),
  resourceType: z.literal("task").describe('Always "task".'),
  title: z.string().describe("Task title after the change."),
  status: z.string().describe("Task status after the change."),
  previousStatus: z
    .string()
    .optional()
    .describe("Status before the change, when this update moved it."),
  changed: z.array(z.string()).describe("Fields this update changed."),
  changes: z
    .record(z.string(), z.object({ from: z.unknown(), to: z.unknown() }))
    .describe("Each changed field, before and after."),
  actor: z
    .object({
      userId: z
        .string()
        .nullable()
        .describe("Public id of the user who changed it, if a person did."),
      agentThreadId: z
        .string()
        .nullable()
        .describe("Agent thread that changed it, if an agent did."),
      workflowRunId: z.string().nullable().describe("Workflow run that changed it, if a run did."),
    })
    .describe("Who changed the task; at most one field is set."),
  triggerChain: z
    .array(z.string())
    .describe(
      "Public ids of the workflows already run in this chain; a workflow in it is never re-entered.",
    ),
  startedAt: z.string().describe("ISO 8601 timestamp when the run was queued."),
});

const platformEventOutputSchema = z
  .object({
    eventType: z.string().describe("Registered platform event type that started the run."),
    resourceId: z.string().describe("Public id of the event subject."),
    resourceType: z.string().describe("Registered type of the event subject."),
    startedAt: z.string().describe("ISO 8601 timestamp when the run was queued."),
  })
  .passthrough();

const eventTriggerContracts = Object.fromEntries(
  Object.keys(WORKFLOW_TRIGGER_CATALOG).map((type) => [
    type,
    {
      inputSchema: triggerInputSchema,
      configSchema: triggerConfigSchema,
      outputSchema: platformEventOutputSchema,
    },
  ]),
) satisfies Record<string, NodeContract>;

export const TRIGGER_NODE_CONTRACTS: Record<string, NodeContract> = {
  ...eventTriggerContracts,
  manual: {
    inputSchema: triggerInputSchema,
    configSchema: triggerConfigSchema,
    outputSchema: manualOutputSchema,
  },
  tag_applied: {
    inputSchema: triggerInputSchema,
    configSchema: triggerConfigSchema,
    outputSchema: tagAppliedOutputSchema,
  },
  email_received: {
    inputSchema: triggerInputSchema,
    configSchema: triggerConfigSchema,
    outputSchema: emailReceivedOutputSchema,
  },
  record_created: {
    inputSchema: triggerInputSchema,
    configSchema: triggerConfigSchema,
    outputSchema: recordCreatedOutputSchema,
  },
  schedule: {
    inputSchema: triggerInputSchema,
    configSchema: triggerConfigSchema,
    outputSchema: scheduleOutputSchema,
  },
  task_updated: {
    inputSchema: triggerInputSchema,
    configSchema: triggerConfigSchema,
    outputSchema: taskUpdatedOutputSchema,
  },
};

export {
  emailReceivedOutputSchema,
  manualOutputSchema,
  recordCreatedOutputSchema,
  scheduleOutputSchema,
  taskUpdatedOutputSchema,
};
